/**
 * Market Data Service
 *
 * Provides REAL market data for newsletter generation using pre-collected data.
 * Enhanced with weekly and quarterly summary generation for AI context.
 * Data flow: Lambda (Python) → S3 (JSON) → Lambda Function URL → Frontend
 */

import HybridCache from './HybridCache.js';
import { enforceContract } from '../types/MarketDataContract.js';
import logger from '../utils/logger.js';

class MarketDataService {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('MarketDataService');

    // Lambda function URL for fetching pre-collected market data from S3.
    // #363: only this variable — the old fallbacks pointed at the placeholder
    // generator's public URL, which is now IAM-only.
    this.lambdaFunctionUrl = import.meta.env?.VITE_MARKET_DATA_LAMBDA_URL;

    this.cache = new HybridCache({
      marketTTL: 24 * 60 * 60 * 1000,  // 24 hours - data is collected daily
      newsTTL: 60 * 60 * 1000,    // 1 hour for news
      economicTTL: 24 * 60 * 60 * 1000,  // 24 hours for economic data
      weeklySummaryTTL: 7 * 24 * 60 * 60 * 1000,  // 7 days
      quarterlySummaryTTL: 90 * 24 * 60 * 60 * 1000  // 90 days
    });

    // Track data availability for UI notifications (CLAUDE.md Rule 15)
    this.isUnavailable = false;

    this.logger.info('Initialized with Lambda function integration', {
      hasLambdaUrl: !!this.lambdaFunctionUrl,
      lambdaUrl: this.lambdaFunctionUrl
    });
  }

  /**
   * Get current unavailability status for UI notifications
   * @returns {boolean} True if market data is currently unavailable
   */
  getUnavailableStatus() {
    return this.isUnavailable;
  }

  /**
   * Get latest market data from pre-collected data
   * TODO: Replace with Lambda/S3 fetch when deployed
   * @param {string} frequency - 'daily', 'weekly', or 'monthly'
   * @returns {Promise<Object>} Market data object
   */
  async getLatestData(frequency = 'daily') {
    try {
      // Check cache first
      const cacheKey = `market_data_${frequency}_${new Date().toISOString().split('T')[0]}_v12`; // v12 — Sprint 6 M4 (#182): Lambda now emits sector_analysis.summary + technical_analysis.summary + investment_insights.summary, force refetch.
      const cached = await this.cache.getContent(cacheKey);
      if (cached) {
        this.logger.debug('Returning cached market data');
        // Preserve isUnavailable flag in cached data
        return cached;
      }

      this.logger.info('Using pre-collected market data (real data from yfinance)');

      // Fetch the pre-collected data (may come from Lambda or fallback)
      // This is REAL market data collected by the market_data_collector.py
      const marketData = await this.getPreCollectedMarketData();

      // Check if data fetch succeeded or failed
      if (marketData.error) {
        // Data unavailable - set flag for UI notifications
        this.isUnavailable = true;
        marketData.isUnavailable = true;
      } else {
        // Data available - clear flag
        this.isUnavailable = false;
        marketData.isUnavailable = false;
      }

      // Enforce contract in development to catch format mismatches early
      enforceContract(marketData, 'MarketDataService.getLatestData()', 'transformed');

      this.logger.info('Successfully loaded pre-collected market data', {
        collectionTimestamp: marketData.collection_timestamp,
        dataSource: marketData.data_source,
        hasIndices: !!marketData.indices,
        hasSectors: !!marketData.sectors,
        hasEconomicData: !!marketData.economicIndicators,
        hasCommodities: !!marketData.commodities,
        hasCurrencies: !!marketData.currencies
      });

      // Cache the result (24 hours - data is collected daily)
      await this.cache.setContent(cacheKey, marketData, { ttl: this.cache.marketTTL });

      return marketData;

    } catch (error) {
      this.logger.error('Error loading market data', error);
      // Data fetch failed - set unavailable flag
      this.isUnavailable = true;

      // Return fallback data structure with clear indication it's fallback
      const fallbackData = this.getFallbackMarketData(frequency);
      fallbackData.isUnavailable = true;
      return fallbackData;
    }
  }

  /**
   * Get pre-collected market data from the market_data_collector
   * This is REAL data collected from yfinance and other sources
   * Fetches from Lambda Function URL which provides the latest market data
   * @returns {Object} Pre-collected market data with fallback metadata
   */
  async getPreCollectedMarketData() {
    // Fetch from Lambda Function URL (primary data source)
    if (this.lambdaFunctionUrl) {
      try {
        this.logger.info('Fetching market data from Lambda function...');
        const response = await fetch(this.lambdaFunctionUrl);

        if (response.ok) {
          const lambdaData = await response.json();

          // Check if Lambda returned an error response
          if (lambdaData.Message !== undefined || lambdaData.error) {
            this.logger.warn('Lambda returned error response:', lambdaData);
            throw new Error('Lambda returned error response');
          }

          this.logger.info('✅ Successfully fetched data from Lambda');

          // Lambda returns market data directly with timestamp
          if (lambdaData && lambdaData.timestamp) {
            // Extract the actual collection timestamp from Lambda response
            const collectionTimestamp = lambdaData.timestamp;

            // Calculate next refresh (24 hours from collection)
            const nextRefresh = new Date(new Date(collectionTimestamp).getTime() + 24 * 60 * 60 * 1000).toISOString();

            // Transform Lambda data to frontend format
            const transformedData = {
              collection_timestamp: collectionTimestamp,
              frequency: 'daily',
              data_source: 'Yahoo Finance, Alpha Vantage',
              dataQuality: {
                score: Math.round((lambdaData.data_quality_score || 0.95) * 100),
                lastRefresh: collectionTimestamp,
                nextRefresh: nextRefresh,
                sources: lambdaData.sources_used || ['Yahoo Finance', 'Alpha Vantage'],
                status: 'healthy'
              }
            };

            // Transform market_indices to indices with proper field names
            if (lambdaData.market_indices) {
              transformedData.indices = {};
              const indexMap = {
                'S&P 500': 'SPY',
                'NASDAQ': 'QQQ',
                'DOW': 'DIA',
                'VIX': 'VIX'
              };

              Object.entries(lambdaData.market_indices).forEach(([name, data]) => {
                const symbol = indexMap[name] || name;
                transformedData.indices[symbol] = {
                  price: data.price || data.value || data.close || 0,
                  change: data.change || 0,
                  changePercent: data.change_percent || 0
                };
              });

              this.logger.info('Transformed indices:', transformedData.indices);
            }

            // Transform sector_performance to sectors (handle both object and array formats)
            if (lambdaData.sector_performance) {
              // If it's a dict with sector names as keys, preserve those names
              if (!Array.isArray(lambdaData.sector_performance) && typeof lambdaData.sector_performance === 'object') {
                transformedData.sectors = Object.entries(lambdaData.sector_performance).map(([sectorName, sectorData]) => ({
                  name: sectorName, // Use the key (sector name) from the dict
                  change: sectorData.week_change || sectorData.change_percent || sectorData.change || 0,
                  price: sectorData.price || 0,
                  volume: sectorData.volume || 0
                }));
              } else {
                // Handle array format (fallback for legacy data)
                transformedData.sectors = lambdaData.sector_performance.map((sector, index) => ({
                  name: sector.name || sector.sector || `Sector ${index}`,
                  change: sector.week_change || sector.change_percent || sector.change || 0,
                  price: sector.price || 0,
                  volume: sector.volume || 0
                }));
              }

              this.logger.info('Transformed sectors:', transformedData.sectors.slice(0, 3));
            }

            // Transform economic_indicators to economicIndicators
            if (lambdaData.economic_indicators) {
              transformedData.economicIndicators = lambdaData.economic_indicators;
            }

            // Transform commodities (map week_change to change and change_percent)
            if (lambdaData.commodity_prices) {
              transformedData.commodities = {};
              Object.entries(lambdaData.commodity_prices).forEach(([name, data]) => {
                transformedData.commodities[name] = {
                  price: data.price || 0,
                  change: data.week_change || data.change || 0, // Dollar change
                  change_percent: data.week_change || data.change_percent || 0, // Percentage change
                  symbol: data.symbol,
                  timestamp: data.timestamp,
                  source: data.source
                };
              });
            }

            // Transform currencies (handle both currency_data (new) and currency_rates (old))
            const currencySource = lambdaData.currency_data || lambdaData.currency_rates || {};
            if (Object.keys(currencySource).length > 0) {
              transformedData.currencies = {};
              Object.entries(currencySource).forEach(([name, data]) => {
                transformedData.currencies[name] = {
                  rate: data.rate || data.price || 0,
                  change: data.week_change || data.change || 0, // Dollar change
                  change_percent: data.week_change || data.change_percent || 0, // Percentage change
                  symbol: data.symbol,
                  timestamp: data.timestamp,
                  source: data.source
                };
              });
              this.logger.info('Transformed currencies:', Object.keys(transformedData.currencies).length, 'pairs');
            } else {
              this.logger.warn('No currency data available (weekend - Forex markets closed)');
              transformedData.currencies = {}; // Set empty object when no data
            }

            // Transform international_markets to international (map change to change_percent)
            if (lambdaData.international_markets) {
              transformedData.international = {};
              Object.entries(lambdaData.international_markets).forEach(([name, data]) => {
                transformedData.international[name] = {
                  price: data.price || 0,
                  change: data.change || 0, // This is percentage from Twelve Data
                  change_percent: data.change || 0, // Percentage change
                  symbol: data.symbol,
                  volume: data.volume || 0,
                  timestamp: data.timestamp,
                  source: data.source
                };
              });
              this.logger.info('Transformed international markets:', Object.keys(transformedData.international));
            } else {
              this.logger.warn('No international_markets in Lambda data');
              transformedData.international = {}; // Set empty object when no data
            }

            this.logger.info('Final transformed data keys:', Object.keys(transformedData));
            return transformedData;
          }
        } else {
          this.logger.warn('Lambda function returned error, using fallback data');
        }
      } catch (error) {
        this.logger.error('Error fetching from Lambda:', error);
        this.logger.info('Falling back to local data');
      }
    }

    // FALLBACK: Show error message instead of hardcoded stale data
    // This prevents users from seeing outdated October 2025 data
    // Per audit recommendation: comprehensive-hardcoded-data-audit-2025-12-17.md
    this.logger.warn('❌ Lambda unavailable and no S3 URL configured');
    this.logger.warn('Returning error state - NO hardcoded fallback data');

    return {
      error: true,
      errorType: 'DATA_UNAVAILABLE',
      message: 'Market data is temporarily unavailable. Please try again in a few minutes.',
      timestamp: new Date().toISOString(),
      isStale: true,
      staleSince: null,
      warning: 'Live market data unavailable - service may be updating',
      // Empty data structure to prevent crashes
      collection_timestamp: new Date().toISOString(),
      frequency: 'daily',
      data_source: 'unavailable',
      dataQuality: {
        score: 0,
        lastRefresh: null,
        nextRefresh: null,
        sources: [],
        status: 'unavailable'
      },
      indices: {},
      international: {},
      sectors: {},
      economicIndicators: {},
      bond_data: {},
      commodities: {},
      currencies: {},
      market_sentiment: {},
      news_highlights: []
    };
  }

  // NOTE: Direct frontend API calls removed per architecture requirements
  // All market data should come from Lambda → S3 → Frontend
  // See CLAUDE.md for architecture details

  /**
   * Parse quote data from API response
   */
  parseQuoteData(promiseResult, name, fallbackPrice) {
    if (promiseResult.status === 'fulfilled' && promiseResult.value) {
      const data = promiseResult.value;
      return {
        close: data.close,
        change: data.change,
        change_percent: data.change_percent
      };
    } else {
      this.logger.warn(`Using fallback data for ${name}`);
      return {
        close: fallbackPrice,
        change: 0,
        change_percent: 0
      };
    }
  }

  /**
   * Get historical market data for charts
   * @param {string} symbol - Market symbol (e.g., '^GSPC', '^IXIC', '^DJI', 'SPY', 'QQQ')
   * @param {string} period - Time period ('1M', '3M', '6M', '1Y', '5Y')
   * @returns {Promise<Object>} Historical data with OHLCV
   */
  async getHistoricalData(symbol, period = '1Y') {
    try {
      // Check cache first
      const cacheKey = `historical_${symbol}_${period}`;
      const cached = await this.cache.getContent(cacheKey);
      if (cached) {
        this.logger.info(`Returning cached historical data for ${symbol} (${period})`);
        return cached;
      }

      this.logger.info(`Fetching historical data for ${symbol} (${period})...`);

      // Convert period to Yahoo Finance range format
      const rangeMap = {
        '1M': '1mo',
        '3M': '3mo',
        '6M': '6mo',
        '1Y': '1y',
        '5Y': '5y'
      };

      const range = rangeMap[period] || '1y';

      // Determine appropriate interval based on period
      const intervalMap = {
        '1M': '1d',
        '3M': '1d',
        '6M': '1d',
        '1Y': '1wk',
        '5Y': '1mo'
      };

      const interval = intervalMap[period] || '1d';

      // Fetch historical data from Yahoo Finance
      const url = `${this.yahooFinanceBase}/${symbol}?interval=${interval}&range=${range}`;

      const response = await fetch(url, {
        headers: {
          'User-Agent': 'Mozilla/5.0'
        }
      });

      if (!response.ok) {
        throw new Error(`Yahoo Finance API error: ${response.status}`);
      }

      const data = await response.json();
      const result = data.chart.result[0];

      if (!result || !result.timestamp || !result.indicators) {
        throw new Error('Invalid historical data received');
      }

      const timestamps = result.timestamp;
      const quote = result.indicators.quote[0];

      // Transform to our format with OHLCV data
      const historicalData = {
        symbol,
        period,
        interval,
        dataPoints: timestamps.map((timestamp, index) => ({
          timestamp: timestamp * 1000, // Convert to milliseconds
          date: new Date(timestamp * 1000).toISOString(),
          open: quote.open[index],
          high: quote.high[index],
          low: quote.low[index],
          close: quote.close[index],
          volume: quote.volume[index]
        })).filter(dp => dp.close !== null), // Remove null data points
        meta: {
          currency: result.meta.currency,
          symbol: result.meta.symbol,
          exchangeName: result.meta.exchangeName,
          instrumentType: result.meta.instrumentType,
          firstTradeDate: result.meta.firstTradeDate,
          regularMarketTime: result.meta.regularMarketTime,
          currentPrice: result.meta.regularMarketPrice,
          previousClose: result.meta.chartPreviousClose
        }
      };

      // Calculate summary statistics
      const closes = historicalData.dataPoints.map(dp => dp.close);
      const high = Math.max(...closes);
      const low = Math.min(...closes);
      const latest = closes[closes.length - 1];
      const first = closes[0];
      const change = latest - first;
      const changePercent = (change / first) * 100;

      historicalData.summary = {
        high,
        low,
        periodChange: change,
        periodChangePercent: changePercent,
        dataPointCount: historicalData.dataPoints.length
      };

      // Cache the result (1 hour for historical data)
      await this.cache.setContent(cacheKey, historicalData, {
        ttl: 60 * 60 * 1000 // 1 hour
      });

      this.logger.info(`Successfully fetched ${historicalData.dataPoints.length} historical data points for ${symbol}`);
      return historicalData;

    } catch (error) {
      this.logger.error(`Error fetching historical data for ${symbol}:`, error);
      throw new Error(`Failed to fetch historical data for ${symbol}: ${error.message}`);
    }
  }

  /**
   * Get historical data for multiple symbols
   * @param {Array<string>} symbols - Array of symbols
   * @param {string} period - Time period
   * @returns {Promise<Object>} Map of symbol to historical data
   */
  async getMultipleHistoricalData(symbols, period = '1Y') {
    try {
      const results = await Promise.allSettled(
        symbols.map(symbol => this.getHistoricalData(symbol, period))
      );

      const historicalDataMap = {};
      results.forEach((result, index) => {
        const symbol = symbols[index];
        if (result.status === 'fulfilled') {
          historicalDataMap[symbol] = result.value;
        } else {
          this.logger.error(`Failed to fetch historical data for ${symbol}`, { error: result.reason });
          historicalDataMap[symbol] = null;
        }
      });

      return historicalDataMap;
    } catch (error) {
      this.logger.error('Error fetching multiple historical data:', error);
      throw error;
    }
  }

  /**
   * Get portfolio performance historical data
   * Useful for charting portfolio vs benchmark
   * @param {string} portfolioSymbol - Portfolio symbol (e.g., 'SPY' for benchmark)
   * @param {string} period - Time period
   * @returns {Promise<Object>} Historical performance data
   */
  async getPortfolioHistoricalPerformance(portfolioSymbol = '^GSPC', period = '1Y') {
    try {
      const historical = await this.getHistoricalData(portfolioSymbol, period);

      // Calculate cumulative returns
      const firstClose = historical.dataPoints[0].close;
      historical.dataPoints.forEach(dp => {
        dp.cumulativeReturn = ((dp.close - firstClose) / firstClose) * 100;
      });

      return historical;
    } catch (error) {
      this.logger.error('Error fetching portfolio historical performance:', error);
      throw error;
    }
  }

  /**
   * Get sector performance
   * @returns {Promise<Object>} Sector performance data
   */
  async getSectorPerformance() {
    const data = await this.getLatestData('daily');
    return data.sector_performance;
  }

  /**
   * Get economic indicators
   * @returns {Promise<Object>} Economic indicators
   */
  async getEconomicIndicators() {
    const data = await this.getLatestData('daily');
    return data.economic_indicators;
  }

  /**
   * Get latest news headlines
   * @param {number} limit - Number of headlines to return
   * @returns {Promise<Array>} News headlines
   */
  async getNewsHeadlines(limit = 5) {
    const data = await this.getLatestData('daily');
    return data.news_highlights.slice(0, limit);
  }

  /**
   * Generate weekly market summary for AI context
   * Output: 40-60 word summary with key market movements, sector performance, and economic data
   * @returns {Promise<Object>} Weekly summary with text and metadata
   */
  async generateWeeklySummary() {
    try {
      // Check cache first
      const cacheKey = `market_summary_weekly_${new Date().toISOString().split('T')[0]}`;
      const cached = await this.cache.getContent('market_summary_weekly', {
        date: new Date().toISOString().split('T')[0]
      });

      if (cached) {
        return cached;
      }

      // Get weekly market data
      const marketData = await this.getLatestData('weekly');

      // Calculate key metrics for the week
      const indices = marketData.market_indices;
      const sectors = marketData.sector_performance;
      const vix = marketData.market_sentiment.vix;
      const yields = marketData.bond_data;

      // Find best and worst performing sectors
      const sectorEntries = Object.entries(sectors);
      const sortedSectors = sectorEntries.sort((a, b) => b[1].week_change - a[1].week_change);
      const bestSector = sortedSectors[0];
      const worstSector = sortedSectors[sortedSectors.length - 1];

      // Generate summary text (40-60 words)
      const summary = `Markets showed ${this._getMarketDirection(indices)} performance this week with the S&P 500 ${indices['S&P 500'].change_percent > 0 ? 'up' : 'down'} ${Math.abs(indices['S&P 500'].change_percent).toFixed(1)}%. ${bestSector[0]} led sectors gaining ${bestSector[1].week_change.toFixed(1)}%, while ${worstSector[0]} lagged at ${worstSector[1].week_change.toFixed(1)}%. The VIX indicates ${vix.level.toLowerCase()} at ${vix.value.toFixed(1)}. Treasury yields ${this._getYieldMovement(yields)}, with the 10-year at ${yields['10-Year Treasury'].yield.toFixed(2)}%.`;

      const result = {
        summary,
        metadata: {
          generated_at: new Date().toISOString(),
          data_period: 'weekly',
          word_count: summary.split(' ').length,
          indices_performance: indices,
          top_sector: bestSector[0],
          bottom_sector: worstSector[0],
          vix_level: vix.value,
          ten_year_yield: yields['10-Year Treasury'].yield
        }
      };

      // Cache the result
      await this.cache.setContent('market_summary_weekly', result, {
        date: new Date().toISOString().split('T')[0],
        ttl: 7 * 24 * 60 * 60 * 1000 // 7 days
      });

      return result;
    } catch (error) {
      this.logger.error('Error generating weekly summary:', error);
      throw new Error('Failed to generate weekly market summary');
    }
  }

  /**
   * Generate quarterly market summary for AI context
   * Output: 60-80 word summary with macro trends, sector rotations, and key themes
   * @returns {Promise<Object>} Quarterly summary with text and metadata
   */
  async generateQuarterlySummary() {
    try {
      // Check cache first
      const quarter = this._getCurrentQuarter();
      const cacheKey = `market_summary_quarterly_${quarter}`;
      const cached = await this.cache.getContent('market_summary_quarterly', {
        quarter: quarter
      });

      if (cached) {
        return cached;
      }

      // Get quarterly data (using monthly as proxy for now)
      const marketData = await this.getLatestData('monthly');
      const economicData = marketData.economic_indicators;

      // Calculate quarterly performance
      const indices = marketData.market_indices;
      const sectors = marketData.sector_performance;

      // Identify key themes based on data
      const themes = this._identifyQuarterlyThemes(marketData);

      // Identify sector rotation patterns
      const sectorRotation = this._analyzeSectorRotation(sectors);

      // Generate summary text (60-80 words)
      const summary = `Q${quarter.split('-Q')[1]} ${quarter.split('-')[0]} marked by ${themes[0].toLowerCase()} with the S&P 500 ${indices['S&P 500'].change_percent > 0 ? 'gaining' : 'declining'} ${Math.abs(indices['S&P 500'].change_percent * 3).toFixed(1)}% for the quarter. ${sectorRotation.narrative}. Economic data ${this._getEconomicTrend(economicData)} with GDP at ${economicData.gdp_growth.value}%, inflation at ${economicData.inflation_cpi.value}%, and unemployment at ${economicData.unemployment_rate.value}%. The Fed funds rate at ${economicData.fed_funds_rate.value}% ${this._getFedStance(economicData)}. Key themes: ${themes.slice(0, 2).join(' and ').toLowerCase()}.`;

      const result = {
        summary,
        metadata: {
          generated_at: new Date().toISOString(),
          data_period: 'quarterly',
          quarter: quarter,
          word_count: summary.split(' ').length,
          quarterly_return: (indices['S&P 500'].change_percent * 3).toFixed(2) + '%',
          key_themes: themes,
          sector_rotation: sectorRotation.pattern,
          economic_indicators: economicData
        }
      };

      // Cache the result
      await this.cache.setContent('market_summary_quarterly', result, {
        quarter: quarter,
        ttl: 90 * 24 * 60 * 60 * 1000 // 90 days
      });

      return result;
    } catch (error) {
      this.logger.error('Error generating quarterly summary:', error);
      throw new Error('Failed to generate quarterly market summary');
    }
  }

  /**
   * Helper: Determine overall market direction
   * @private
   */
  _getMarketDirection(indices) {
    const changes = Object.values(indices).map(idx => idx.change_percent);
    const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;

    if (avgChange > 0.5) return 'strong positive';
    if (avgChange > 0) return 'mixed-to-positive';
    if (avgChange > -0.5) return 'mixed-to-negative';
    return 'broadly negative';
  }

  /**
   * Helper: Describe yield movement
   * @private
   */
  _getYieldMovement(yields) {
    const tenYear = yields['10-Year Treasury'].change;
    const twoYear = yields['2-Year Treasury'].change;

    if (tenYear > 5 && twoYear > 5) return 'rose sharply';
    if (tenYear > 0 && twoYear > 0) return 'moved higher';
    if (tenYear < -5 && twoYear < -5) return 'fell sharply';
    if (tenYear < 0 && twoYear < 0) return 'declined';
    return 'showed mixed moves';
  }

  /**
   * Helper: Get current quarter
   * @private
   */
  _getCurrentQuarter() {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    return `${now.getFullYear()}-Q${quarter}`;
  }

  /**
   * Helper: Identify quarterly themes
   * @private
   */
  _identifyQuarterlyThemes(data) {
    const themes = [];

    // Check volatility
    if (data.market_sentiment.vix.value > 20) {
      themes.push('Elevated volatility');
    } else if (data.market_sentiment.vix.value < 15) {
      themes.push('Low volatility environment');
    }

    // Check inflation trend
    if (data.economic_indicators.inflation_cpi.value > data.economic_indicators.inflation_cpi.prev) {
      themes.push('Rising inflation pressures');
    } else {
      themes.push('Moderating inflation');
    }

    // Check market sentiment
    const fearGreed = data.market_sentiment.fear_greed_index.value;
    if (fearGreed > 70) {
      themes.push('Strong risk appetite');
    } else if (fearGreed < 30) {
      themes.push('Risk-off sentiment');
    }

    // Add sector-specific theme
    const techPerf = data.sector_performance['Technology'].month_change;
    if (techPerf > 5) {
      themes.push('Tech sector leadership');
    }

    return themes.length > 0 ? themes : ['Balanced market conditions'];
  }

  /**
   * Helper: Analyze sector rotation
   * @private
   */
  _analyzeSectorRotation(sectors) {
    const sorted = Object.entries(sectors)
      .sort((a, b) => b[1].month_change - a[1].month_change);

    const leaders = sorted.slice(0, 2).map(s => s[0]);
    const laggards = sorted.slice(-2).map(s => s[0]);

    const narrative = `Rotation into ${leaders.join(' and ')} while ${laggards.join(' and ')} underperformed`;

    return {
      pattern: `${leaders[0]} -> ${leaders[1]}`,
      narrative,
      leaders,
      laggards
    };
  }

  /**
   * Helper: Describe economic trend
   * @private
   */
  _getEconomicTrend(indicators) {
    const gdp = indicators.gdp_growth.value;
    const inflation = indicators.inflation_cpi.value;

    if (gdp > 2.5 && inflation < 3) return 'shows healthy expansion';
    if (gdp > 2.5 && inflation > 3) return 'indicates overheating risks';
    if (gdp < 1.5 && inflation < 2) return 'suggests slowdown';
    if (gdp < 1.5 && inflation > 3) return 'points to stagflation concerns';
    return 'remains mixed';
  }

  /**
   * Helper: Interpret Fed stance
   * @private
   */
  _getFedStance(indicators) {
    const rate = indicators.fed_funds_rate.value;
    const inflation = indicators.inflation_cpi.value;

    if (rate > 5 && inflation > 3) return 'maintains restrictive stance';
    if (rate > 5 && inflation < 3) return 'signals potential easing ahead';
    if (rate < 3) return 'remains accommodative';
    return 'holds steady policy';
  }

  /**
   * Transform Lambda response data to match frontend format
   * @private
   */
  transformLambdaData(lambdaData) {
    if (!lambdaData) {
      return {};
    }

    const transformed = {
      collection_timestamp: lambdaData.timestamp,
      frequency: 'daily',
      dataSource: lambdaData.sources_used?.join(', ') || 'Unknown',
    };

    // Transform market_indices to indices with proper field names
    if (lambdaData.market_indices) {
      transformed.indices = {};
      const indexMap = {
        'S&P 500': 'SPY',
        'NASDAQ': 'QQQ',
        'DOW': 'DIA',
        'VIX': 'VIX'
      };

      Object.entries(lambdaData.market_indices).forEach(([name, data]) => {
        const symbol = indexMap[name] || name;
        transformed.indices[symbol] = {
          price: data.price || data.value || data.close || 0,
          change: data.change || 0,
          changePercent: data.change_percent || 0
        };
      });
    }

    // Transform sectors from sector_performance (can be array or object)
    if (lambdaData.sector_performance) {
      if (Array.isArray(lambdaData.sector_performance)) {
        // Handle array format: [{ name: 'Tech', week_change: 3.5 }, ...]
        transformed.sectors = lambdaData.sector_performance.map(sector => ({
          name: sector.name || sector.sector,
          change: sector.week_change || sector.change_percent || 0,
          change_percent: sector.week_change || sector.change_percent || 0,
          price: sector.price || 0,
          volume: sector.volume || 0,
        }));
      } else if (typeof lambdaData.sector_performance === 'object') {
        // Handle object format: { 'Technology': { week_change: 3.5 }, ... }
        transformed.sectors = Object.entries(lambdaData.sector_performance).map(([name, data]) => ({
          name: name,
          change: data.week_change || data.change_percent || 0,
          change_percent: data.week_change || data.change_percent || 0,
          price: data.price || 0,
          volume: data.volume || 0,
        }));
      }
    }

    // Transform commodities from commodity_prices object
    if (lambdaData.commodity_prices) {
      transformed.commodities = {};
      Object.entries(lambdaData.commodity_prices).forEach(([name, data]) => {
        transformed.commodities[name] = {
          price: data.price || 0,
          change: data.week_change || 0,
          change_percent: data.week_change || 0,
          symbol: data.symbol || '',
          timestamp: data.timestamp || lambdaData.timestamp,
          source: data.source || 'Unknown',
        };
      });
    }

    // Transform currencies from currency_data object (can be empty on weekends)
    if (lambdaData.currency_data !== undefined) {
      transformed.currencies = {};
      Object.entries(lambdaData.currency_data).forEach(([pair, data]) => {
        transformed.currencies[pair] = {
          rate: data.rate || 0,
          change: data.week_change || 0,
          change_percent: data.week_change || 0,
          symbol: data.symbol || '',
          timestamp: data.timestamp || lambdaData.timestamp,
          source: data.source || 'Unknown',
        };
      });
    }

    // Transform international markets from international_markets object
    if (lambdaData.international_markets) {
      transformed.international = {};
      Object.entries(lambdaData.international_markets).forEach(([name, data]) => {
        transformed.international[name] = {
          price: data.price || 0,
          change: data.change || 0,
          change_percent: data.change || 0,
          symbol: data.symbol || '',
          volume: data.volume || 0,
          timestamp: data.timestamp || lambdaData.timestamp,
          source: data.source || 'Unknown',
        };
      });
    }

    // Transform economic indicators
    if (lambdaData.economic_indicators) {
      transformed.economicIndicators = lambdaData.economic_indicators;
    }

    // Add data quality metadata
    if (lambdaData.timestamp) {
      const collectionTime = new Date(lambdaData.timestamp);
      const nextRefresh = new Date(collectionTime.getTime() + 24 * 60 * 60 * 1000).toISOString();

      transformed.dataQuality = {
        score: Math.round((lambdaData.data_quality_score || 0.95) * 100),
        lastRefresh: lambdaData.timestamp,
        nextRefresh: nextRefresh,
        sources: lambdaData.sources_used || ['Yahoo Finance'],
        status: 'healthy',
      };
    }

    return transformed;
  }

  /**
   * Get latest market snapshot for dashboard
   * @returns {Promise<Object>} Latest market snapshot
   */
  async getLatestSnapshot() {
    return await this.getLatestData('daily');
  }

  /**
   * Refresh market data (force new fetch)
   * @returns {Promise<void>}
   */
  async refreshData() {
    // Simply return fresh data without cache (cache will auto-update)
    // Since we're using pre-collected data, we don't need to clear cache
    return await this.getLatestData('daily');
  }

  // NOTE: Sector, economic, and FRED API methods removed per architecture
  // All data should come from Lambda → S3 → Frontend flow

  /**
   * Get fallback economic indicators when API fails
   * @private
   */
  getFallbackEconomicIndicators() {
    return {
      unemployment_rate: {
        value: 3.7,
        prev: 3.8,
        date: new Date().toISOString().split('T')[0]
      },
      inflation_cpi: {
        value: 3.2,
        prev: 3.7,
        date: new Date().toISOString().split('T')[0]
      },
      gdp_growth: {
        value: 2.4,
        prev: 2.1,
        date: this._getCurrentQuarter()
      },
      fed_funds_rate: {
        value: 5.5,
        date: new Date().toISOString().split('T')[0]
      }
    };
  }

  // NOTE: Bond, news API methods removed per architecture
  // All data should come from Lambda → S3 → Frontend flow

  /**
   * Get fallback news headlines when API fails
   * @private
   */
  getFallbackNewsHeadlines() {
    return [
      {
        headline: 'Markets monitor Federal Reserve policy signals',
        source: 'Financial News',
        date: new Date().toISOString(),
        sentiment: 'neutral'
      },
      {
        headline: 'Technology sector shows resilience amid rate concerns',
        source: 'Market Watch',
        date: new Date().toISOString(),
        sentiment: 'positive'
      },
      {
        headline: 'Inflation data comes in line with expectations',
        source: 'Economic Report',
        date: new Date().toISOString(),
        sentiment: 'neutral'
      },
      {
        headline: 'Energy stocks rally on supply concerns',
        source: 'Bloomberg',
        date: new Date().toISOString(),
        sentiment: 'positive'
      },
      {
        headline: 'Consumer spending data beats forecasts',
        source: 'Reuters',
        date: new Date().toISOString(),
        sentiment: 'positive'
      }
    ];
  }

  // NOTE: Commodities API methods removed per architecture
  // All data should come from Lambda → S3 → Frontend flow

  /**
   * Get fallback commodities data
   * @private
   */
  getFallbackCommodities() {
    return {
      'WTI Crude': { price: 72.45, change: 0, change_percent: 0 },
      'Gold': { price: 2035.50, change: 0, change_percent: 0 },
      'Silver': { price: 23.85, change: 0, change_percent: 0 },
      'Natural Gas': { price: 2.95, change: 0, change_percent: 0 },
      'Copper': { price: 3.87, change: 0, change_percent: 0 },
      'Brent Crude': { price: 76.20, change: 0, change_percent: 0 }
    };
  }

  // NOTE: Currency API methods removed per architecture
  // All data should come from Lambda → S3 → Frontend flow

  /**
   * Get fallback currency data
   * @private
   */
  getFallbackCurrencies() {
    return {
      'EUR/USD': { rate: 1.0875, change: 0, change_percent: 0 },
      'GBP/USD': { rate: 1.2654, change: 0, change_percent: 0 },
      'USD/JPY': { rate: 149.85, change: 0, change_percent: 0 },
      'USD/CHF': { rate: 0.8765, change: 0, change_percent: 0 },
      'AUD/USD': { rate: 0.6523, change: 0, change_percent: 0 },
      'USD/CAD': { rate: 1.3654, change: 0, change_percent: 0 }
    };
  }

  /**
   * Helper: Calculate Fear & Greed Index
   */
  calculateFearGreedIndex(sp500Change, vix) {
    // Simplified calculation based on market metrics
    let score = 50; // Start neutral

    // Market momentum (±20 points)
    score += Math.min(Math.max(sp500Change * 10, -20), 20);

    // Volatility (±30 points) - lower VIX = more greed
    const vixNormalized = (vix - 15) / 15; // Normalize around 15
    score -= Math.min(Math.max(vixNormalized * 30, -30), 30);

    return Math.min(Math.max(Math.round(score), 0), 100);
  }

  /**
   * Helper: Get Fear & Greed level
   */
  getFearGreedLevel(value) {
    if (value < 25) return 'Extreme Fear';
    if (value < 45) return 'Fear';
    if (value < 55) return 'Neutral';
    if (value < 75) return 'Greed';
    return 'Extreme Greed';
  }

  /**
   * Helper: Get volatility level
   */
  getVolatilityLevel(vix) {
    if (vix < 12) return 'Very Low';
    if (vix < 20) return 'Low';
    if (vix < 30) return 'Moderate';
    if (vix < 40) return 'High';
    return 'Very High';
  }

  /**
   * Get fallback market data when API fails
   */
  getFallbackMarketData(frequency) {
    this.logger.warn('Using fallback market data');
    return {
      collection_timestamp: new Date().toISOString(),
      frequency,
      data_source: 'Fallback Data',
      dataQuality: {
        score: 85,
        lastRefresh: new Date().toISOString(),
        nextRefresh: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        sources: ['Fallback Data'],
        status: 'degraded'
      },
      // Frontend expects 'indices' with 'price' and 'changePercent' (camelCase)
      indices: {
        'SPY': { price: 4763.21, change: 0, changePercent: 0 },
        'QQQ': { price: 15194.89, change: 0, changePercent: 0 },
        'DIA': { price: 37305.16, change: 0, changePercent: 0 },
        'VIX': { price: 14.82, change: 0, changePercent: 0 }
      },
      sectors: this.getFallbackSectorData(),
      economicIndicators: this.getFallbackEconomicIndicators(),
      bond_data: {
        '10-Year Treasury': { yield: 4.18, change: 0, date: new Date().toISOString() },
        '2-Year Treasury': { yield: 4.85, change: 0, date: new Date().toISOString() }
      },
      market_sentiment: {
        vix: { value: 14.82, level: 'Low' },
        fear_greed_index: { value: 50, level: 'Neutral' }
      },
      news_highlights: this.getFallbackNewsHeadlines(),
      commodities: this.getFallbackCommodities(),
      currencies: this.getFallbackCurrencies()
    };
  }

  getFallbackSectorData() {
    return {
      'Technology': { day_change: 0, week_change: 0, month_change: 0 },
      'Healthcare': { day_change: 0, week_change: 0, month_change: 0 },
      'Financials': { day_change: 0, week_change: 0, month_change: 0 },
      'Energy': { day_change: 0, week_change: 0, month_change: 0 },
      'Consumer': { day_change: 0, week_change: 0, month_change: 0 }
    };
  }
}

// Create singleton instance
const marketDataServiceInstance = new MarketDataService();

// Check feature flag for singleton behavior
const USE_SINGLETON = import.meta.env?.VITE_USE_MARKET_DATA_SINGLETON !== 'false' &&
                      process.env.REACT_APP_USE_MARKET_DATA_SINGLETON !== 'false';

// Export based on feature flag
export default USE_SINGLETON ? marketDataServiceInstance : MarketDataService;

// Always export the class for testing purposes
export { MarketDataService };
